<script>
import CKEditor from '@ckeditor/ckeditor5-vue2'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import {
    eventReasonSelectOptions,
    eventTypeSelectOptions,
    lineupPlayersForTeam,
    periodSelectOptions,
    withSelectedOption,
} from './liveblogFormOptions'
import { LIVEBLOG_CONTENT_TYPES } from '@/components/liveblog/liveblogFormModel'

export default {
    components: {
        ckeditor: CKEditor.component,
        vueDropzone: vue2Dropzone,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        match: {
            type: Object,
            default: null,
        },
        eventTypes: {
            type: Array,
            default: () => [],
        },
        periods: {
            type: Array,
            default: () => [],
        },
        eventReasons: {
            type: Array,
            default: () => [],
        },
        matchLineups: {
            type: Object,
            default: null,
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
        showCommonFields: {
            type: Boolean,
            default: false,
        },
        hideTypeSelector: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            contentTypes: LIVEBLOG_CONTENT_TYPES,
            galleryItems: [{ url: '', file: null, preview: '' }],
            imagePreview: '',
            imageFileError: '',
            acceptedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'],
            maxImageSizeKb: 4096,
            editor: ClassicEditor,
            editorConfig: {
                toolbar: [
                    'heading',
                    '|',
                    'bold',
                    'italic',
                    'link',
                    '|',
                    'bulletedList',
                    'numberedList',
                    'blockQuote',
                    '|',
                    'undo',
                    'redo',
                ],
            },
            galleryDropzoneOptions: {
                url: '/',
                autoProcessQueue: false,
                addRemoveLinks: true,
                maxFilesize: 4,
                maxFiles: 1,
                acceptedFiles: 'image/jpeg,image/png,image/gif,image/webp,image/bmp',
                uploadMultiple: false,
                clickable: true,
                thumbnailWidth: 150,
                thumbnailMethod: 'contain',
                dictDefaultMessage: 'Drop image here or click to upload',
                dictRemoveFile: 'Remove',
                dictMaxFilesExceeded: 'Only one file per gallery item',
                dictFileTooBig: 'Image is larger than 4MB',
            },
        }
    },
    computed: {
        form: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        teamOptions() {
            if (!this.match) return []
            return [
                { value: this.match.home_team_code, label: `${this.match.home_team_code} (Home)` },
                { value: this.match.away_team_code, label: `${this.match.away_team_code} (Away)` },
            ]
        },
        displayCommonFields() {
            return this.showCommonFields || !!this.form.content_type
        },
        lineupPlayerOptions() {
            return lineupPlayersForTeam(
                this.matchLineups,
                this.form.team_code,
                this.match,
                this.form.place
            )
        },
        playerSelectOptions() {
            return withSelectedOption(
                this.lineupPlayerOptions,
                this.form.player_id,
                this.form.player_name
            )
        },
        relatedPlayerSelectOptions() {
            return withSelectedOption(
                this.lineupPlayerOptions,
                this.form.related_player_id,
                this.form.related_player_name
            )
        },
        eventReasonOptions() {
            return eventReasonSelectOptions(
                this.eventReasons,
                this.form.event_reason_id,
                this.form.event_reason_name
            )
        },
        eventTypeOptions() {
            return eventTypeSelectOptions(
                this.eventTypes,
                this.form.event_type_id,
                this.form.event_type_name
            )
        },
        periodOptions() {
            return periodSelectOptions(this.periods, this.form.period_id, this.form.period_name)
        },
        titleHtml: {
            get() {
                return this.form.title || ''
            },
            set(val) {
                this.updateField('title', val)
            },
        },
        bodyHtml: {
            get() {
                return this.form.body || ''
            },
            set(val) {
                this.updateField('body', val)
            },
        },
        playerIdModel: {
            get() {
                return this.form.player_id != null && this.form.player_id !== ''
                    ? String(this.form.player_id)
                    : ''
            },
            set(val) {
                this.onPlayerChange(val)
            },
        },
        relatedPlayerIdModel: {
            get() {
                return this.form.related_player_id != null && this.form.related_player_id !== ''
                    ? String(this.form.related_player_id)
                    : ''
            },
            set(val) {
                this.onRelatedPlayerChange(val)
            },
        },
        playerListHint() {
            if (!this.match) return ''
            if (!this.form.team_code && !this.form.place) {
                return 'Select a team or place (Home/Away) to load players.'
            }
            if (!this.lineupPlayerOptions.length) {
                return 'No players found for this team. Check match lineups or squad.'
            }
            return ''
        },
    },
    watch: {
        'form.content_type'(type, oldType) {
            if (oldType === 'image' && type !== 'image') {
                this.clearImageSelection()
            }
            if (type === 'gallery') {
                if (this.galleryItems.length === 0) {
                    this.galleryItems = [this.emptyGalleryItem()]
                }
                this.syncGalleryPayload()
            } else if (oldType === 'gallery') {
                this.clearGalleryItems()
            }
        },
        value: {
            immediate: true,
            deep: true,
            handler(val) {
                if (val.content_type === 'gallery') {
                    if (val.gallery_items && val.gallery_items.length) {
                        this.galleryItems = val.gallery_items.map((item) => ({
                            url: item.url || '',
                            file: item.file || null,
                            preview: item.preview || item.url || '',
                        }))
                    } else if (val.payload && val.payload.images && val.payload.images.length) {
                        this.galleryItems = val.payload.images.map((img) =>
                            this.emptyGalleryItem(img.url || '')
                        )
                    } else if (this.galleryItems.length === 0) {
                        this.galleryItems = [this.emptyGalleryItem()]
                    }
                } else {
                    this.clearGalleryItems()
                }
                if (val.content_type === 'image' && val.payload && val.payload.url && !val.image_file) {
                    this.revokeImagePreview()
                    this.imagePreview = val.payload.url
                }
            },
        },
    },
    methods: {
        onEventTypeChange(eventTypeId) {
            const match = this.eventTypeOptions.find((et) => et.value === String(eventTypeId))
            this.$emit('input', {
                ...this.form,
                event_type_id: eventTypeId,
                event_type_name: match ? match.label : '',
            })
        },
        onPeriodChange(periodId) {
            const match = this.periodOptions.find((p) => p.value === String(periodId))
            this.$emit('input', {
                ...this.form,
                period_id: periodId,
                period_name: match ? match.label : '',
            })
        },
        onTeamCodeChange(teamCode) {
            let place = this.form.place
            let team_id = ''
            if (this.match) {
                if (teamCode === this.match.home_team_code) {
                    place = 'home'
                    team_id =
                        this.match.home_team_id != null && this.match.home_team_id !== ''
                            ? String(this.match.home_team_id)
                            : ''
                } else if (teamCode === this.match.away_team_code) {
                    place = 'away'
                    team_id =
                        this.match.away_team_id != null && this.match.away_team_id !== ''
                            ? String(this.match.away_team_id)
                            : ''
                }
            }
            this.$emit('input', this.syncPlayersForSide({ team_code: teamCode, place, team_id }))
        },
        onPlaceChange(place) {
            let team_code = this.form.team_code
            let team_id = this.form.team_id != null && this.form.team_id !== '' ? String(this.form.team_id) : ''
            if (this.match) {
                if (place === 'home') {
                    team_code = this.match.home_team_code || team_code
                    team_id =
                        this.match.home_team_id != null && this.match.home_team_id !== ''
                            ? String(this.match.home_team_id)
                            : team_id
                } else if (place === 'away') {
                    team_code = this.match.away_team_code || team_code
                    team_id =
                        this.match.away_team_id != null && this.match.away_team_id !== ''
                            ? String(this.match.away_team_id)
                            : team_id
                }
            }
            this.$emit('input', this.syncPlayersForSide({ place, team_code, team_id }))
        },
        onPlayerChange(playerId) {
            const id = playerId != null && playerId !== '' ? String(playerId) : ''
            const match = this.playerSelectOptions.find((p) => p.value === id)
            this.$emit('input', {
                ...this.form,
                player_id: id,
                player_name: match ? match.label : '',
            })
        },
        onRelatedPlayerChange(playerId) {
            const id = playerId != null && playerId !== '' ? String(playerId) : ''
            const match = this.relatedPlayerSelectOptions.find((p) => p.value === id)
            this.$emit('input', {
                ...this.form,
                related_player_id: id,
                related_player_name: match ? match.label : '',
            })
        },
        syncPlayersForSide(partial) {
            const next = { ...this.form, ...partial }
            const options = lineupPlayersForTeam(
                this.matchLineups,
                next.team_code,
                this.match,
                next.place
            )
            const optionIds = new Set(options.map((o) => o.value))
            if (next.player_id && !optionIds.has(String(next.player_id))) {
                next.player_id = ''
                next.player_name = ''
            }
            if (next.related_player_id && !optionIds.has(String(next.related_player_id))) {
                next.related_player_id = ''
                next.related_player_name = ''
            }
            return next
        },
        updateField(key, val) {
            this.$emit('input', { ...this.form, [key]: val })
        },
        updatePayloadField(key, val) {
            const payload = { ...(this.form.payload || {}), [key]: val }
            this.updateField('payload', payload)
        },
        emptyGalleryItem(url = '') {
            return {
                url: url || '',
                file: null,
                preview: url || '',
            }
        },
        clearGalleryItems() {
            this.galleryItems.forEach((item) => this.revokeGalleryPreview(item))
            this.galleryItems = [this.emptyGalleryItem()]
            this.imageFileError = ''
        },
        revokeGalleryPreview(item) {
            if (item && item.preview && item.preview.startsWith('blob:')) {
                URL.revokeObjectURL(item.preview)
            }
        },
        validateImageFile(file) {
            if (!file) return 'No file selected'
            const sizeKb = file.size / 1024
            if (sizeKb > this.maxImageSizeKb) {
                return `File too large. Maximum size is ${this.maxImageSizeKb} KB.`
            }
            if (!this.acceptedImageTypes.includes(file.type)) {
                return 'Invalid file type. Use PNG, JPG, GIF, WEBP, or BMP.'
            }
            return null
        },
        getGalleryReadyItems() {
            return this.galleryItems.filter(
                (item) =>
                    item.file ||
                    (item.url && String(item.url).trim() && /^https?:\/\//i.test(String(item.url).trim()))
            )
        },
        syncGalleryPayload() {
            if (this.form.content_type !== 'gallery') return
            const ready = this.getGalleryReadyItems()
            const images = ready.map((item) => ({
                url: item.file ? '' : String(item.url).trim(),
            }))
            this.updateField('payload', { images })
            this.updateField('gallery_items', this.galleryItems.map((item) => ({
                url: item.url,
                file: item.file,
                preview: item.preview,
            })))
        },
        addGalleryItem() {
            this.galleryItems.push(this.emptyGalleryItem())
            this.syncGalleryPayload()
        },
        removeGalleryItem(index) {
            this.revokeGalleryPreview(this.galleryItems[index])
            this.galleryItems.splice(index, 1)
            if (this.galleryItems.length === 0) {
                this.galleryItems = [this.emptyGalleryItem()]
            }
            this.syncGalleryPayload()
        },
        onGalleryUrlInput(index, value) {
            const item = { ...this.galleryItems[index], url: value }
            if (item.file) {
                this.revokeGalleryPreview(item)
                item.file = null
                this.clearGalleryDropzone(index)
            }
            item.preview = value || ''
            this.$set(this.galleryItems, index, item)
            this.imageFileError = ''
            this.syncGalleryPayload()
        },
        onGalleryFileSelect(index, event) {
            const file = event.target.files && event.target.files[0]
            if (!file) return
            const error = this.validateImageFile(file)
            if (error) {
                this.imageFileError = error
                return
            }
            this.imageFileError = ''
            const item = { ...this.galleryItems[index] }
            this.revokeGalleryPreview(item)
            item.file = file
            item.url = ''
            item.preview = URL.createObjectURL(file)
            this.$set(this.galleryItems, index, item)
            if (event.target) event.target.value = ''
            this.syncGalleryPayload()
        },
        clearGalleryItem(index) {
            this.revokeGalleryPreview(this.galleryItems[index])
            this.$set(this.galleryItems, index, this.emptyGalleryItem())
            this.imageFileError = ''
            this.clearGalleryDropzone(index)
            this.syncGalleryPayload()
        },
        getGalleryDropzoneRef(index) {
            const ref = this.$refs[`gallery-dropzone-${index}`]
            if (Array.isArray(ref)) return ref[0]
            return ref
        },
        clearGalleryDropzone(index) {
            const dz = this.getGalleryDropzoneRef(index)
            if (dz && typeof dz.removeAllFiles === 'function') {
                dz.removeAllFiles(true)
            }
        },
        onGalleryDropzoneAdded(index, file) {
            const error = this.validateImageFile(file)
            if (error) {
                this.imageFileError = error
                this.clearGalleryDropzone(index)
                return
            }
            this.imageFileError = ''
            const item = { ...this.galleryItems[index] }
            this.revokeGalleryPreview(item)
            item.file = file
            item.url = ''
            item.preview = URL.createObjectURL(file)
            this.$set(this.galleryItems, index, item)
            this.syncGalleryPayload()
        },
        onGalleryDropzoneRemoved(index) {
            const item = this.galleryItems[index]
            if (!item || !item.file) return
            this.revokeGalleryPreview(item)
            this.$set(this.galleryItems, index, this.emptyGalleryItem())
            this.syncGalleryPayload()
        },
        onGalleryDropzoneError(index, file, message) {
            this.imageFileError = typeof message === 'string' ? message : 'Invalid image file'
            this.clearGalleryDropzone(index)
        },
        galleryUrlDisplay(item) {
            return item.file ? '' : item.url
        },
        revokeImagePreview() {
            if (this.imagePreview && this.imagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(this.imagePreview)
            }
        },
        getSingleImageDropzoneRef() {
            const ref = this.$refs['single-image-dropzone']
            if (Array.isArray(ref)) return ref[0]
            return ref
        },
        clearSingleImageDropzone() {
            const dz = this.getSingleImageDropzoneRef()
            if (dz && typeof dz.removeAllFiles === 'function') {
                dz.removeAllFiles(true)
            }
        },
        clearImageSelection() {
            this.revokeImagePreview()
            this.imagePreview = ''
            this.imageFileError = ''
            this.$emit('input', { ...this.form, image_file: null, payload: null })
            this.clearSingleImageDropzone()
        },
        onImageUrlInput(value) {
            if (this.form.image_file) {
                this.clearSingleImageDropzone()
            }
            this.revokeImagePreview()
            this.imagePreview = value || ''
            this.imageFileError = ''
            this.$emit('input', {
                ...this.form,
                image_file: null,
                payload: value ? { url: value } : null,
            })
        },
        onSingleImageDropzoneAdded(file) {
            const error = this.validateImageFile(file)
            if (error) {
                this.imageFileError = error
                this.clearSingleImageDropzone()
                return
            }
            this.imageFileError = ''
            this.revokeImagePreview()
            this.imagePreview = URL.createObjectURL(file)
            this.$emit('input', {
                ...this.form,
                image_file: file,
                payload: null,
            })
        },
        onSingleImageDropzoneRemoved() {
            if (!this.form.image_file) return
            this.clearImageSelection()
        },
        onSingleImageDropzoneError(file, message) {
            this.imageFileError = typeof message === 'string' ? message : 'Invalid image file'
            this.clearSingleImageDropzone()
        },
        imageUrlValue() {
            if (this.form.image_file) return ''
            return (this.form.payload && this.form.payload.url) || ''
        },
    },
}
</script>

<template>
    <div class="liveblog-form-fields">
        <section v-if="!hideTypeSelector" class="marketing-card">
            <header class="marketing-card__header">
                <h3>Entry type</h3>
                <p>Choose what kind of liveblog entry you are creating</p>
            </header>
            <b-form-group label="Type" label-class="fw-semibold" class="mb-0">
                <b-form-select
                    :value="form.content_type"
                    :disabled="isEdit"
                    :options="contentTypes.map((t) => ({ value: t.value, text: t.label }))"
                    @change="updateField('content_type', $event)"
                />
            </b-form-group>
        </section>

        <section v-if="form.content_type" class="marketing-card">
            <header class="marketing-card__header">
                <h3>Content</h3>
                <p>Title and body shown on the match timeline</p>
            </header>
            <b-form-group class="mb-4">
                <template #label>
                    Title
                    <span v-if="form.content_type === 'text'" class="text-danger">*</span>
                </template>
                <div class="liveblog-editor">
                    <ckeditor :editor="editor" v-model="titleHtml" :config="editorConfig"></ckeditor>
                </div>
            </b-form-group>
            <b-form-group class="mb-0">
                <template #label>
                    Body
                    <span v-if="form.content_type === 'text'" class="text-danger">*</span>
                </template>
                <div class="liveblog-editor">
                    <ckeditor :editor="editor" v-model="bodyHtml" :config="editorConfig"></ckeditor>
                </div>
            </b-form-group>
        </section>

        <section v-if="form.content_type === 'image'" class="marketing-card">
            <header class="marketing-card__header marketing-card__header--icon">
                <i class="uil uil-image"></i>
                <div>
                    <h3>Image</h3>
                    <p>Paste a URL or upload a file for this entry</p>
                </div>
            </header>
            <div class="marketing-gallery-item">
                <input
                    class="form-control mb-2"
                    :value="imageUrlValue()"
                    :disabled="!!form.image_file"
                    placeholder="Paste image URL or upload below"
                    @input="onImageUrlInput($event.target.value)"
                />
                <vue-dropzone
                    ref="single-image-dropzone"
                    id="liveblog-single-image-dropzone"
                    class="mb-2"
                    :options="galleryDropzoneOptions"
                    @vdropzone-file-added="onSingleImageDropzoneAdded"
                    @vdropzone-removed-file="onSingleImageDropzoneRemoved"
                    @vdropzone-error="onSingleImageDropzoneError($event[0], $event[2])"
                />
                <button
                    v-if="form.image_file || (form.payload && form.payload.url) || imagePreview"
                    type="button"
                    class="btn btn-sm btn-outline-secondary mb-2"
                    @click="clearImageSelection"
                >
                    Clear
                </button>
                <div v-if="imagePreview" class="mt-1">
                    <img :src="imagePreview" alt="Preview" class="img-fluid rounded border" style="max-height: 120px" />
                </div>
            </div>
            <p class="text-muted small mt-2 mb-0">Use URL or upload — not both.</p>
            <p v-if="imageFileError" class="text-danger small mt-2 mb-0">{{ imageFileError }}</p>
        </section>

        <section v-if="form.content_type === 'event'" class="marketing-card">
            <header class="marketing-card__header">
                <h3>Match event</h3>
                <p>Link this entry to players, period, and event type</p>
            </header>
            <div class="row">
            <div class="col-md-6 mb-3">
                <label class="form-label">Event type</label>
                <select
                    class="form-select"
                    :value="form.event_type_id"
                    @change="onEventTypeChange($event.target.value)"
                >
                    <option value="">Select event type</option>
                    <option
                        v-for="et in eventTypeOptions"
                        :key="`event-type-${et.value}`"
                        :value="et.value"
                    >
                        {{ et.label }}
                    </option>
                </select>
            </div>
            <div class="col-md-6 mb-3">
                <label class="form-label">Period</label>
                <select
                    class="form-select"
                    :value="form.period_id"
                    @change="onPeriodChange($event.target.value)"
                >
                    <option value="">Select period</option>
                    <option v-for="p in periodOptions" :key="`period-${p.value}`" :value="p.value">
                        {{ p.label }}
                    </option>
                </select>
            </div>
            <div class="col-md-6 mb-3">
                <label class="form-label">Team</label>
                <select
                    class="form-select"
                    :value="form.team_code"
                    @change="onTeamCodeChange($event.target.value)"
                >
                    <option value="">Select team</option>
                    <option v-for="t in teamOptions" :key="t.value" :value="t.value">{{ t.label }}</option>
                </select>
            </div>
            <div class="col-md-6 mb-3">
                <label class="form-label">Place</label>
                <select class="form-select" :value="form.place" @change="onPlaceChange($event.target.value)">
                    <option value="">Auto</option>
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                </select>
            </div>
            <div class="col-md-6 mb-3">
                <label class="form-label">Player</label>
                <select v-model="playerIdModel" class="form-select">
                    <option value="">Select player</option>
                    <option v-for="p in playerSelectOptions" :key="`player-${p.value}`" :value="p.value">
                        {{ p.label }}
                    </option>
                </select>
                <p v-if="playerListHint" class="text-muted small mb-0 mt-1">{{ playerListHint }}</p>
            </div>
            <div class="col-md-6 mb-3">
                <label class="form-label">Related player</label>
                <select v-model="relatedPlayerIdModel" class="form-select">
                    <option value="">Select related player</option>
                    <option v-for="p in relatedPlayerSelectOptions" :key="`related-${p.value}`" :value="p.value">
                        {{ p.label }}
                    </option>
                </select>
            </div>
            <div class="col-md-6 mb-3">
                <label class="form-label">Event reason</label>
                <select
                    class="form-select"
                    :value="form.event_reason_id"
                    @change="updateField('event_reason_id', $event.target.value)"
                >
                    <option value="">Select event reason</option>
                    <option
                        v-for="r in eventReasonOptions"
                        :key="`reason-${r.value}`"
                        :value="r.value"
                    >
                        {{ r.label }}
                    </option>
                </select>
            </div>
            <div class="col-12 mb-0">
                <label class="form-label">Comment</label>
                <textarea class="form-control" rows="2" :value="form.comment" @input="updateField('comment', $event.target.value)"></textarea>
            </div>
            </div>
        </section>

        <section v-if="form.content_type === 'gallery'" class="marketing-card">
            <header class="marketing-card__header marketing-card__header--icon">
                <i class="uil uil-images"></i>
                <div>
                    <h3>Gallery</h3>
                    <p>Add one or more images — URL or file per image</p>
                </div>
            </header>
            <div
                v-for="(item, index) in galleryItems"
                :key="`gallery-item-${index}`"
                class="marketing-gallery-item"
            >
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong class="small">Image {{ index + 1 }}</strong>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        :disabled="galleryItems.length === 1"
                        title="Remove"
                        @click="removeGalleryItem(index)"
                    >
                        <i class="uil uil-trash-alt"></i>
                    </button>
                </div>
                <input
                    class="form-control mb-2"
                    :value="galleryUrlDisplay(item)"
                    :disabled="!!item.file"
                    placeholder="Paste image URL or upload below"
                    @input="onGalleryUrlInput(index, $event.target.value)"
                />
                <vue-dropzone
                    :ref="`gallery-dropzone-${index}`"
                    :id="`liveblog-gallery-dropzone-${index}`"
                    class="mb-2"
                    :options="galleryDropzoneOptions"
                    @vdropzone-file-added="onGalleryDropzoneAdded(index, $event)"
                    @vdropzone-removed-file="onGalleryDropzoneRemoved(index)"
                    @vdropzone-error="onGalleryDropzoneError(index, $event[0], $event[2])"
                />
                <button
                    v-if="item.file || item.url || item.preview"
                    type="button"
                    class="btn btn-sm btn-outline-secondary mb-2"
                    @click="clearGalleryItem(index)"
                >
                    Clear
                </button>
                <div v-if="item.preview" class="mt-1">
                    <img :src="item.preview" alt="" class="img-fluid rounded border" style="max-height: 120px" />
                </div>
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="addGalleryItem">
                <i class="mdi mdi-plus me-1"></i>Add image
            </button>
            <p v-if="getGalleryReadyItems().length" class="text-muted small mt-2 mb-0">
                {{ getGalleryReadyItems().length }} image(s) ready to save
            </p>
            <p v-if="imageFileError" class="text-danger small mt-2 mb-0">{{ imageFileError }}</p>
        </section>

        <section v-if="form.content_type === 'video'" class="marketing-card">
            <header class="marketing-card__header">
                <h3>Video</h3>
                <p>Embed or link to match video content</p>
            </header>
            <div class="row">
            <div class="col-md-6 mb-3">
                <label class="form-label">Video URL</label>
                <input class="form-control" :value="form.payload && form.payload.url" @input="updatePayloadField('url', $event.target.value)" />
            </div>
            <div class="col-md-6 mb-3">
                <label class="form-label">Embed ID</label>
                <input class="form-control" :value="form.payload && form.payload.embed_id" @input="updatePayloadField('embed_id', $event.target.value)" />
            </div>
            <div class="col-md-6 mb-0">
                <label class="form-label">Provider</label>
                <input class="form-control" :value="form.payload && form.payload.provider" @input="updatePayloadField('provider', $event.target.value)" />
            </div>
            </div>
        </section>

        <section v-if="form.content_type === 'twitter'" class="marketing-card">
            <header class="marketing-card__header">
                <h3>Twitter</h3>
                <p>Embed a tweet on the timeline</p>
            </header>
            <div class="row">
            <div class="col-md-6 mb-3">
                <label class="form-label">Tweet URL</label>
                <input class="form-control" :value="form.payload && form.payload.url" @input="updatePayloadField('url', $event.target.value)" />
            </div>
            <div class="col-md-6 mb-3">
                <label class="form-label">Tweet ID</label>
                <input class="form-control" :value="form.payload && form.payload.tweet_id" @input="updatePayloadField('tweet_id', $event.target.value)" />
            </div>
            <div class="col-md-6 mb-0">
                <label class="form-label">Author</label>
                <input class="form-control" :value="form.payload && form.payload.author" @input="updatePayloadField('author', $event.target.value)" />
            </div>
            </div>
        </section>

        <section v-if="form.content_type === 'link'" class="marketing-card">
            <header class="marketing-card__header">
                <h3>Link</h3>
                <p>External link shown on the timeline</p>
            </header>
            <div class="row">
            <div class="col-md-6 mb-3">
                <label class="form-label">URL <span class="text-danger">*</span></label>
                <input class="form-control" :value="form.payload && form.payload.url" @input="updatePayloadField('url', $event.target.value)" />
            </div>
            <div class="col-md-6 mb-0">
                <label class="form-label">Label</label>
                <input class="form-control" :value="form.payload && form.payload.label" @input="updatePayloadField('label', $event.target.value)" />
            </div>
            </div>
        </section>

        <section v-if="form.content_type === 'highlight'" class="marketing-card">
            <header class="marketing-card__header">
                <h3>Highlight</h3>
                <p>Video highlight URL and optional thumbnail</p>
            </header>
            <div class="row">
            <div class="col-md-6 mb-3">
                <label class="form-label">Highlight URL <span class="text-danger">*</span></label>
                <input class="form-control" :value="form.payload && form.payload.url" @input="updatePayloadField('url', $event.target.value)" />
            </div>
            <div class="col-md-6 mb-0">
                <label class="form-label">Thumbnail URL</label>
                <input class="form-control" :value="form.payload && form.payload.thumbnail" @input="updatePayloadField('thumbnail', $event.target.value)" />
            </div>
            </div>
        </section>

        <section v-if="form.content_type" class="marketing-card">
            <header class="marketing-card__header">
                <h3>Timing &amp; score</h3>
                <p>Minute and score shown with this entry</p>
            </header>
            <div class="row g-3">
                <div class="col-md-4">
                    <label class="form-label">Minute</label>
                    <input class="form-control" :value="form.event_minute" @input="updateField('event_minute', $event.target.value)" placeholder="e.g. 45" />
                </div>
                <div class="col-md-4">
                    <label class="form-label">Home goals</label>
                    <input type="number" class="form-control" :value="form.home_goals" @input="updateField('home_goals', $event.target.value)" />
                </div>
                <div class="col-md-4">
                    <label class="form-label">Away goals</label>
                    <input type="number" class="form-control" :value="form.away_goals" @input="updateField('away_goals', $event.target.value)" />
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/marketing-modal';

.liveblog-editor >>> .ck-toolbar {
    padding: 2px 4px !important;
    min-height: 0 !important;
}
.liveblog-editor >>> .ck-toolbar .ck-button {
    width: 26px;
    height: 26px;
}
.liveblog-editor >>> .ck-toolbar .ck-button .ck-icon {
    width: 14px;
    height: 14px;
}
.liveblog-editor >>> .ck-editor__main > .ck-editor__editable,
.liveblog-editor >>> .ck-editor__editable_inline {
    min-height: 48px !important;
    max-height: 72px !important;
    height: 72px !important;
    overflow-y: auto !important;
    font-size: 0.875rem;
    line-height: 1.3;
    padding: 4px 8px !important;
}
.liveblog-editor >>> p {
    margin: 0;
}
.liveblog-form-fields >>> .vue-dropzone {
    border: 2px dashed #d3d8e6;
    border-radius: 12px;
    background: #fff;
    min-height: 120px;
}
</style>
